/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { SidebarContent } from '@/components/blocks/SideBar/parts/Content';
import { Box, Breakpoint, Skeleton, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FC, PropsWithChildren, useCallback, useContext, useEffect } from 'react';
import { DpDrawer } from '@/components/blocks/DpDrawer';
import { ContextModal } from '@/data/context/content';
import { ID } from '@/data/types/Basic';

type Props = {
	title: string;
	id?: ID;
	href?: string;
	mobileBreakpoint: Breakpoint;
	scrollable?: boolean;
	total?: number;
	loading?: boolean;
};

export const Sidebar: FC<PropsWithChildren<Props>> = ({
	title,
	id,
	href,
	mobileBreakpoint = 'sm',
	scrollable = false,
	children,
	total,
	loading = false,
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down(mobileBreakpoint));
	const context = useContext(ContextModal);
	const { state, onSetTotal, onSetOpen } = context || {};

	const toggleOpen = useCallback(() => {
		if (isMobile) onSetOpen?.(!!state?.open);
	}, [onSetOpen, state?.open, isMobile]);

	useEffect(() => {
		onSetTotal?.(total ?? 0);
	}, [onSetTotal, total]);

	return isMobile ? (
		<DpDrawer
			title={
				<Box component="span" display="flex" alignItems="center" gap="8px">
					{title}
					{total ? ` (${total.toLocaleString('es-MX')})` : <Skeleton variant="text" width="60px" />}
				</Box>
			}
			onClose={() => onSetOpen?.(false)}
			open={!!state?.open}
			onApply={() => onSetOpen?.(false)}
		>
			<SidebarContent
				{...{ children, href, isMobile, scrollable, title, toggleOpen, total, id, loading }}
			/>
		</DpDrawer>
	) : (
		<SidebarContent
			{...{ children, href, isMobile, scrollable, title, toggleOpen, total, id, loading }}
		/>
	);
};
